/* eslint-disable no-use-before-define */
import { Card, Space, Spin, Typography } from 'antd';
import LocaleContext from 'locales';
import { isWindows, vsClagentPortCheck } from 'pages/utils';
import React, { useContext, useEffect } from 'react';
import { getClientRegistry } from 'services/vsclient';
import { checkPreAuth, doAutoLogin, getBioAuthUser } from 'services/websocket/auth';
import ConfigContext from 'store/ConfigContext';
import packageJson from '../../../package.json';

/**
 * 로그인 전에 pre-check을 수행한다. (#196183)
 *
 * 생체인증을 위해 빈페이지를 띄운 뒤 id 체크를 진행한다
 * 1. 토큰이 있으면 로그인페이지로 리다이렉션
 * 2. 토큰이 없으면 clagnet에게 id 요청 후 id를 mgmt에게 보내 인증 요청
 * @returns
 */
function PreCheck({ history, location, config }) {
  const fconfig = useContext(ConfigContext);
  const { locale } = useContext(LocaleContext);
  const useBioAuth = config?.features?.bioAuth?.use ?? false;

  const token = localStorage.getItem('token');
  const checkedAutoLogin = localStorage.getItem('autoLogin');

  const redirectLoginPage = (message = undefined) => {
    localStorage.removeItem('token');

    history.push({
      pathname: '/auth/login',
      message,
      preChecked: true,

      redirectUrlToVd: location?.redirectUrlToVd,
    });
  };

  const redirectDesktopPage = () => {
    localStorage.setItem('autoConnect', true);
    localStorage.setItem('autoLogin', true);
    localStorage.setItem('redirectComplete', false);

    history.push({
      pathname: '/desktops',
      redirectUrlToVd: location?.redirectUrlToVd,
    });
  };

  const requestPreCheck = async (userId) => {
    try {
      const preCheckRes = await checkPreAuth(userId);

      return preCheckRes;
    } catch (e) {
      return {
        success: false,
        message: useBioAuth ? 'txt.bio-auth-msg.conn-refuesed' : 'txt.precheck.failed',
      };
    }
  };

  const checkBioAuthTarget = async (userId) => {
    try {
      const bioAuthUserRes = await getBioAuthUser(userId);

      if (bioAuthUserRes.success) {
        const { registeredQr, registeredOtp, registeredFace, registeredPattern } =
          bioAuthUserRes.data?.user;

        const isRequireBioAuth =
          registeredQr || registeredOtp || registeredFace || registeredPattern;

        return { isRequireBioAuth, message: '' };
      }

      return { isRequireBioAuth: false, message: bioAuthUserRes.message };
    } catch (e) {
      console.error(e.stack || e);
      return { isRequireBioAuth: false, message: e.message };
    }
  };

  // TODO #191683 모바일 접속인 경우 다른 방안 필요
  const requestUserIdAtRegistry = async () => {
    const regKeys = [
      'HKEY_CURRENT_USER\\Volatile Environment\\USERNAME',
      'HKEY_LOCAL_MACHINE\\SOFTWARE\\Microsoft\\Windows\\CurrentVersion\\Authentication\\LogonUI\\LastLoggedOnDisplayName',
    ];

    try {
      if (isWindows) {
        await vsClagentPortCheck();
      }
      const userIdRequestRes = await getClientRegistry(regKeys);

      const userId = userIdRequestRes.data[regKeys[0]];
      const userName = userIdRequestRes.data[regKeys[1]];

      // clagent에서 userId를 찾지 못했다면 생체인증 대상자 판별이 불가하니 아니니 그냥 로그인페이지로 보낸다.
      if (!userId) {
        localStorage.setItem('autoLogin', 'false');
        redirectLoginPage();
        return null;
      }

      return { userId, userName };
    } catch (e) {
      redirectLoginPage('txt.check-required-program');

      return null;
    }
  };

  const tryBioAuth = async () => {
    const userData = await requestUserIdAtRegistry();

    if (!userData) {
      return null;
    }
    const { userId } = userData;
    localStorage.setItem('userId', userId);
    localStorage.setItem('latestLocalUser', userId);

    const { isRequireBioAuth, message } = await checkBioAuthTarget(userId);

    // 생체인증 대상자 여부 기록
    localStorage.setItem('bioAuth', !!isRequireBioAuth);

    if (!isRequireBioAuth) {
      return { success: false, message };
    }

    const preCheckResult = await requestPreCheck(userId);

    if (!preCheckResult.success) {
      return { success: preCheckResult.success, message: preCheckResult.message };
    }

    return { success: preCheckResult.success, token: preCheckResult.token };
  };

  /**
   * 자동 로그인 전 로컬 PC 사용자가 토큰 발급 사용자와 일치하는지 확인한다.
   * 최근 로그인 사용자와 현재 로컬 유저가 같으면 true
   */
  const isLocalUserLatestLogined = async () => {
    try {
      const regKeys = ['HKEY_CURRENT_USER\\Volatile Environment\\USERNAME'];
      if (isWindows) {
        await vsClagentPortCheck();
      }
      const userReg = await getClientRegistry(regKeys);

      const currentUser = userReg.data[regKeys[0]];

      const latestLocalUser = localStorage.getItem('latestLocalUser');

      return currentUser === latestLocalUser;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const requestAutoLogin = async (data) => {
    try {
      const res = await doAutoLogin(data);

      return res;
    } catch (e) {
      console.error(e);

      return null;
    }
  };

  const tryAutoLogin = async (data) => {
    const isLatestLogined = await isLocalUserLatestLogined();

    // 생체 인증이 활성화 되어있는데 로컬 사용자와 토큰 발급 사용자가 다르면 생체인증 로직을 태운다.
    if (useBioAuth && !isLatestLogined) {
      return { success: false };
    }

    const autoLoginResult = await requestAutoLogin(data);

    if (!autoLoginResult) {
      return { success: false };
    }

    return { success: true, data: autoLoginResult };
  };

  // * 생체 인증 O -> 생체 인증 대상 확인(auth/check-bio-auth-target) -> pre-check 진행
  const tryBioAuthCheck = async () => {
    // 토큰이 있으면 자동로그인을 시도한다.
    if (token && checkedAutoLogin === 'true' && fconfig.autoLogin) {
      const autoLoginResult = await tryAutoLogin({ token, type: AUTO_LOGIN_TYPES.AUTO_LOGIN });

      if (autoLoginResult.success) {
        localStorage.setItem('username', autoLoginResult.data.username);

        redirectDesktopPage();

        return;
      }
    }

    // 자동로그인에 실패하거나, 자동로그인 대상이 아닌 경우(토큰이 없거나 체크박스 체크가 안된경우)인데 생체인증이 활성화 되어있다면 생체인증을 태운다.
    let redirectMessage = '';
    const bioAuthResult = await tryBioAuth();

    if (bioAuthResult?.success) {
      const autoLoginResult = await tryAutoLogin({
        token: bioAuthResult.token,
        type: AUTO_LOGIN_TYPES.BIO_AUTH,
      });

      // 생체인증에 성공하면 그 토큰으로 자동로그인을 시도해본다. (사용자 로그 기록 및 토큰 검증을 위함)
      if (autoLoginResult.success) {
        localStorage.setItem('token', bioAuthResult.token);
        localStorage.setItem('username', autoLoginResult.data.username);

        redirectDesktopPage();
        return;
      }
    }

    redirectMessage = bioAuthResult?.message;

    redirectLoginPage(redirectMessage);
  };

  // * 생체 인증 X -> pre-check만 진행
  const tryPreCheck = async () => {
    const res = await requestPreCheck();

    if (res?.success) {
      const autoLoginResult = await tryAutoLogin({
        token: res.token,
        type: AUTO_LOGIN_TYPES.SSO_LOGIN,
      });

      // preCheck에 성공하면 그 토큰으로 자동로그인을 시도해본다. (사용자 로그 기록 및 토큰 검증을 위함)
      if (autoLoginResult.success) {
        localStorage.setItem('userId', autoLoginResult.data.userId);
        localStorage.setItem('token', res.token);
        localStorage.setItem('username', autoLoginResult.data.username);
        redirectDesktopPage();
        return;
      }
      redirectLoginPage(autoLoginResult.data.msg);
    }

    // preCheck에 실패 && 자동로그인 옵션 체크 되어있는 경우
    // 토큰으로 자동로그인을 시도해본다. (현재는 하나은행만 사용)
    if (token && checkedAutoLogin === 'true' && fconfig.autoLogin) {
      const autoLoginResult = await tryAutoLogin({ token, type: AUTO_LOGIN_TYPES.AUTO_LOGIN });

      if (autoLoginResult.data.status !== 'success') {
        redirectLoginPage(autoLoginResult.data.msg);
        return;
      }

      if (autoLoginResult.success) {
        localStorage.setItem('username', autoLoginResult.data.username);

        redirectDesktopPage();

        return;
      }
    }

    const message = res?.message;

    redirectLoginPage(message !== 'login.fail.sso-no-credential-cache' ? message : undefined);
  };

  useEffect(() => {
    if (config) {
      if (
        packageJson?.config?.preid === 'lgensol-usa' &&
        sessionStorage.getItem('isExternalAccess') === null
      ) {
        history.push({
          pathname: '/auth/external-access',
        });
        return;
      }
      // TODO 사이트별 체크 사항을 정의한 후 preCheck 돌린다
      // eslint-disable-next-line no-unused-expressions
      useBioAuth ? tryBioAuthCheck() : tryPreCheck();
    }
  }, [config]);

  return (
    <Card>
      <div className="allCenter">
        <Space direction="vertical" align="center" style={{ marginBlock: '0% 10%' }}>
          <Spin spinning size="large" />
          <Typography.Title level={5}>{locale.resource['txt.app-starting']}</Typography.Title>
        </Space>
      </div>
    </Card>
  );
}

const AUTO_LOGIN_TYPES = {
  AUTO_LOGIN: 0, // 자동 로그인 체크박스
  SSO_LOGIN: 1, // SSO
  BIO_AUTH: 2, // 내부 인증(생체 인증)
};

export default PreCheck;
